import type {
  SessionTimelineBlock,
  SessionTimelineEvent,
} from '@kirin/api-client/contracts/agent-sessions';
import { formatDateTimeParts } from '@/lib/date-utils';

/**
 * Display helpers for the agent session timeline (KIR-4445).
 *
 * The timeline's intro sentence is product copy;
 * apps/docs/docs/threat-detection-and-response/agent-session-timeline.mdx
 * quotes the same text, so change both together.
 */

export const SESSION_TIMELINE_OMISSIONS_SENTENCE =
  'This timeline shows the events in the session. The event that triggered the alert is highlighted.';

/** Hover hint on the alert panel's Session Timeline button. */
export const SESSION_TIMELINE_BUTTON_HINT =
  'Open every event in the agent session around this alert, in order, with the alert’s event highlighted.';

export const KEEP_UNAVAILABLE_HINT = 'Available once the session is recorded.';

export const KEEP_SESSION_TOOLTIP =
  "Keep this session's events past retention. Without it, they are deleted automatically 30 days after the session's last event.";

export const KEPT_SESSION_TOOLTIP =
  "This session's events are kept and not deleted automatically. Click to stop keeping them.";

export const RELEASE_SESSION_CONFIRM_TITLE = 'Stop keeping session events?';

export const RELEASE_SESSION_CONFIRM_DESCRIPTION =
  "The events will be deleted automatically 30 days after the session's last event. If the last event was more than 30 days ago, the session will be deleted and cannot be recovered.";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** The header's retention line: how long until the session is deleted. */
export function formatRetention(
  retained: boolean,
  expiresAt: string | null | undefined,
  now: Date = new Date()
): string | null {
  if (retained) return 'Kept, not deleted automatically';
  if (!expiresAt) return null;
  const msLeft = new Date(expiresAt).getTime() - now.getTime();
  if (msLeft <= 0) return 'Due for deletion';
  const days = Math.ceil(msLeft / MS_PER_DAY);
  return `Deleted in ${days} day${days === 1 ? '' : 's'} unless kept`;
}

/** The only finding outcome that means the agent was stopped. */
export const BLOCKED_OUTCOME = 'prevented';

/** How many times a pending highlight is refetched before "not recorded". */
export const PENDING_REFETCH_LIMIT = 5;

export const PENDING_REFETCH_INTERVAL_MS = 3000;

const EVENT_TYPE_BADGES: Record<string, string> = {
  PreUserPrompt: 'Prompt',
  PreShellExecution: 'Command',
  PostShellExecution: 'Command result',
  PreFileRead: 'Read',
  PostFileRead: 'Read result',
  PreFileWrite: 'Edit',
  PostFileEdit: 'Edit result',
  PreMCPToolUse: 'MCP call',
  PostMCPToolUse: 'MCP result',
  PostAIResponse: 'Response',
  PostToolUse: 'Result',
};

/** Each platform's hook names for the event that carries a tool's result. */
const TOOL_RESULT_EVENT_NAMES = new Set([
  'PostToolUse',
  'postToolUse',
  'AfterTool',
]);

/** Windsurf Cascade hooks that fire after a tool ran but carry no result. */
const TOOL_DONE_EVENT_NAMES = new Set([
  'post_run_command',
  'post_write_code',
  'post_read_code',
]);

/**
 * Type badge for a row. `ToolObserved` (tools no processor handles) takes the
 * tool name; anything unknown falls back to its raw event type, so a new agent
 * event type still renders as a generic row.
 */
export function eventBadgeLabel(event: SessionTimelineEvent): string {
  const known = EVENT_TYPE_BADGES[event.eventType];
  if (known) return known;
  if (event.eventType === 'ToolObserved') {
    const tool = event.toolName ?? 'Tool';
    const hook = event.platformEventName ?? '';
    if (TOOL_RESULT_EVENT_NAMES.has(hook)) return `${tool} result`;
    if (TOOL_DONE_EVENT_NAMES.has(hook)) return `${tool} done`;
    return tool;
  }
  return event.eventType;
}

export function blockingFinding(
  event: SessionTimelineEvent
): SessionTimelineBlock | undefined {
  return event.findings.find(f => f.outcome === BLOCKED_OUTCOME);
}

/**
 * The finding a row explains: the blocking one when the event was stopped,
 * else the first detection (e.g. a Monitor-mode alert).
 */
export function rowFinding(
  event: SessionTimelineEvent
): SessionTimelineBlock | undefined {
  return blockingFinding(event) ?? event.findings[0];
}

/**
 * The time half of the drawer header's timestamps: the display zone, the
 * 12/24-hour setting and the zone's short name ("09:00:00 UTC", "5:00:00 AM
 * EDT"), so a row names its zone on its own too.
 */
export function formatClockTime(iso: string, timeZone: string): string {
  return formatDateTimeParts(iso, timeZone)?.time ?? '—';
}

/** "+1m 5s from alert event" / "-12s from alert event" / "At the alert event". */
export function formatOffsetFromAlert(
  iso: string,
  alertIso: string | null
): string | undefined {
  if (!alertIso) return undefined;
  const deltaMs = new Date(iso).getTime() - new Date(alertIso).getTime();
  if (Number.isNaN(deltaMs)) return undefined;
  if (deltaMs === 0) return 'At the alert event';
  const sign = deltaMs > 0 ? '+' : '-';
  let seconds = Math.round(Math.abs(deltaMs) / 1000);
  const hours = Math.floor(seconds / 3600);
  seconds -= hours * 3600;
  const minutes = Math.floor(seconds / 60);
  seconds -= minutes * 60;
  const parts = [
    hours ? `${hours}h` : '',
    minutes ? `${minutes}m` : '',
    seconds || (!hours && !minutes) ? `${seconds}s` : '',
  ].filter(Boolean);
  return `${sign}${parts.join(' ')} from alert event`;
}
